<template>
  <div class="color-selector">
    <!-- Trigger button -->
    <button
      class="color-trigger-btn"
      @click.prevent="toggleDropdown"
      :aria-expanded="state.colorSelectionOpen"
    >
      <div class="color-trigger-preview">
        <span
          v-if="selectedColor"
          class="color-trigger-swatch"
          :style="{ background: selectedColor }"
        ></span>
        <span v-else class="color-trigger-placeholder">
          <font-awesome-icon
            :icon="['fas', 'palette']"
            style="font-size: 13px; opacity: 0.4"
          />
        </span>
        <span class="color-trigger-label">{{
          selectedColor ? 'Change Color' : buttonText
        }}</span>
      </div>
      <font-awesome-icon
        :icon="['fas', 'caret-down']"
        class="color-trigger-caret"
        :class="{ rotated: state.colorSelectionOpen }"
      />
    </button>

    <!-- Color grid panel -->
    <transition name="color-panel-slide">
      <div v-if="state.colorSelectionOpen" class="color-panel">
        <div class="color-grid">
          <div
            v-for="color in colorOptions"
            :key="color"
            class="color-swatch"
            :class="{ selected: selectedColor === color }"
            :style="{ background: color }"
            :title="color"
            @click.prevent="selectColor(color)"
          >
            <font-awesome-icon
              v-if="selectedColor === color"
              :icon="['fas', 'check']"
              class="color-check"
            />
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { reactive, ref } from 'vue';

export default {
  name: 'ColorSelection',
  props: {
    color: {
      type: String,
      default: 'grey-outline-dropdown',
    },
    buttonText: {
      type: String,
      required: true,
    },
    options: {
      type: String,
      default: () => '',
    },
    styleClass: {
      type: String,
      default: '',
    },
  },
  emits: ['optionSelected'],
  setup(props, { emit }) {
    const colorOptions = reactive([
      '#8B0000',
      '#B22222',
      '#DC143C',
      '#FF0000',
      '#FF4500',
      '#FF6347',
      '#FF7F50',
      '#FFA07A',
      '#8B4513',
      '#A0522D',
      '#CD853F',
      '#FF8C00',
      '#FFA500',
      '#FFB347',
      '#FFD700',
      '#FFDAB9',
      '#DAA520',
      '#FFFF00',
      '#FFFFE0',
      '#FFF68F',
      '#FFFACD',
      '#FFFAF0',
      '#FFFDD0',
      '#006400',
      '#228B22',
      '#32CD32',
      '#00FF00',
      '#7CFC00',
      '#90EE90',
      '#98FB98',
      '#E0FFBF',
      '#000080',
      '#0000CD',
      '#1E90FF',
      '#4169E1',
      '#87CEEB',
      '#ADD8E6',
      '#B0E0E6',
      '#E6F2FF',
      '#4B0082',
      '#6A5ACD',
      '#7B68EE',
      '#8A2BE2',
      '#9370DB',
      '#BA55D3',
      '#D8BFD8',
      '#E6E6FA',
      '#800080',
      '#8B008B',
      '#9400D3',
      '#DA70D6',
      '#DDA0DD',
      '#EE82EE',
      '#F3E5F5',
      '#C71585',
      '#DB7093',
      '#FF1493',
      '#FF69B4',
      '#FFB6C1',
      '#FFC0CB',
      '#FFD1DC',
      '#FFF0F5',
      '#5D4037',
      '#6D4C41',
      '#795548',
      '#8D6E63',
      '#A1887F',
      '#BCAAA4',
      '#D7CCC8',
      '#EFEBE9',
      '#212121',
      '#424242',
      '#616161',
      '#757575',
      '#9E9E9E',
      '#BDBDBD',
      '#E0E0E0',
      '#F5F5F5',
      '#006064',
      '#00838F',
      '#0097A7',
      '#00ACC1',
      '#00BCD4',
      '#26C6DA',
      '#4DD0E1',
      '#B2EBF2',
      '#B44328',
      '#D35400',
      '#E67E22',
      '#FF7F50',
      '#FFA07A',
      '#FFB347',
      '#FFDAB9',
      '#00504D',
      '#00695C',
      '#00796B',
      '#009688',
      '#4DB6AC',
      '#80CBC4',
      '#B2DFDB',
      '#E0F2F1',
      '#5D50A8',
      '#7E57C2',
      '#9C27B0',
      '#BA68C8',
      '#CE93D8',
      '#E1BEE7',
      '#F3E5F5',
      '#F8F4FF',
      '#5D1049',
      '#7D1B4E',
      '#9C2C5B',
      '#B93B5F',
      '#D44C73',
      '#E66B89',
      '#F18BA0',
      '#F8BBCF',
    ]);

    const state = reactive({ colorSelectionOpen: false });
    let selectedColor = ref('');

    const toggleDropdown = () => {
      state.colorSelectionOpen = !state.colorSelectionOpen;
    };

    const selectColor = (color) => {
      selectedColor.value = color;
      state.colorSelectionOpen = false;
      emit('optionSelected', color);
    };

    if (props.options.length > 0) {
      selectedColor.value = props.options;
    }

    return { state, toggleDropdown, selectedColor, selectColor, colorOptions };
  },
};
</script>

<style scoped>
.color-selector {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.color-trigger-btn {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  border: 1.5px solid #e7e7e7;
  border-radius: 10px;
  padding: 10px 12px;
  background: white;
  cursor: pointer;
  font-family: 'Poppins', sans-serif;
  transition:
    border-color 0.2s,
    box-shadow 0.2s;
  box-sizing: border-box;
}
.dark-theme .color-trigger-btn {
  background: #171e29;
  border-color: #2a3a4e;
}
.color-trigger-btn:hover {
  border-color: #7f77dd;
}
.color-trigger-btn:focus {
  outline: none;
  border-color: #7f77dd;
  box-shadow: 0 0 0 3px rgba(127, 119, 221, 0.1);
}

.color-trigger-preview {
  display: flex;
  align-items: center;
  gap: 10px;
}

.color-trigger-swatch {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: block;
  border: 2px solid rgba(0, 0, 0, 0.1);
  flex-shrink: 0;
}
.color-trigger-placeholder {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #f4f4f8;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #888;
  flex-shrink: 0;
}
.dark-theme .color-trigger-placeholder {
  background: #2a3a4e;
}

.color-trigger-label {
  font-size: 0.85rem;
  font-weight: 500;
  color: #213547;
}
.dark-theme .color-trigger-label {
  color: #d4d2d2;
}

.color-trigger-caret {
  color: #888;
  font-size: 0.85rem;
  transition: transform 0.2s ease;
}
.color-trigger-caret.rotated {
  transform: rotate(180deg);
  color: var(--color-accent-purple);
}

.color-panel {
  margin-top: 6px;
  background: white;
  border: 1.5px solid #e7e7e7;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
  padding: 0.75rem;
  z-index: 10;
}
.dark-theme .color-panel {
  background: #171e29;
  border-color: #2a3a4e;
}

.color-grid {
  display: grid;
  grid-template-columns: repeat(8, 1fr);
  gap: 8px;
  max-height: 28vh;
  overflow-y: auto;
}
.color-grid::-webkit-scrollbar {
  width: 4px;
}
.color-grid::-webkit-scrollbar-track {
  background: transparent;
}
.color-grid::-webkit-scrollbar-thumb {
  background: #e0e0e0;
  border-radius: 2px;
}
.dark-theme .color-grid::-webkit-scrollbar-thumb {
  background: #2a3a4e;
}

.color-swatch {
  aspect-ratio: 1;
  border-radius: 50%;
  cursor: pointer;
  border: 2px solid transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  transition:
    transform 0.15s,
    border-color 0.15s;
  position: relative;
}
.color-swatch:hover {
  transform: scale(1.15);
  border-color: rgba(255, 255, 255, 0.6);
}
.color-swatch.selected {
  border-color: white;
  box-shadow: 0 0 0 2px #7f77dd;
  transform: scale(1.1);
}

.color-check {
  color: white;
  font-size: 9px;
  filter: drop-shadow(0 1px 1px rgba(0, 0, 0, 0.4));
}

.color-panel-slide-enter-active,
.color-panel-slide-leave-active {
  transition: all 0.2s cubic-bezier(0.34, 1.2, 0.64, 1);
}
.color-panel-slide-enter-from,
.color-panel-slide-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}
</style>

<!-- <template>
  <div class="dropdown-container w-100">
    <button
      :class="['dropdown-button w-100', color, styleClass]"
      @click.prevent="toggleDropdown"
    >
      <span v-if="selectedColor"
        ><div
          class="selected-color-box"
          :style="{ background: selectedColor }"
        ></div
      ></span>
      <span v-else>{{ buttonText }}</span>
      <span class="arrow" :class="{ active: state.colorSelectionOpen }"
        ><font-awesome-icon :icon="['fas', 'caret-down']"
      /></span>
    </button>
    <div v-show="state.colorSelectionOpen" class="colors-container-wrapper">
      <div class="colors-grid-wrapper">
        <div
          v-for="color in colorOptions"
          :key="color"
          class="color-box"
          :style="{ background: color }"
          @click.prevent="selectColor(color)"
        ></div>
      </div>
    </div>
  </div>
</template>

<script>
import { reactive, ref } from 'vue';

export default {
  name: 'ColorSelection',
  props: {
    color: {
      type: String,
      default: 'grey-outline-dropdown',
    },
    buttonText: {
      type: String,
      required: true,
    },
    options: {
      type: String,
      default: () => '',
    },
    styleClass: {
      type: String,
      default: '',
    },
  },
  emits: ['optionSelected'],
  setup(props, { emit }) {
    const colorOptions = reactive([
      '#8B0000',
      '#B22222',
      '#DC143C',
      '#FF0000',
      '#FF4500',
      '#FF6347',
      '#FF7F50',
      '#FFA07A',
      '#8B4513',
      '#A0522D',
      '#CD853F',
      '#FF8C00',
      '#FFA500',
      '#FFB347',
      '#FFD700',
      '#FFDAB9',
      '#DAA520',
      '#FFD700',
      '#FFFF00',
      '#FFFFE0',
      '#FFF68F',
      '#FFFACD',
      '#FFFAF0',
      '#FFFDD0',
      '#006400',
      '#228B22',
      '#32CD32',
      '#00FF00',
      '#7CFC00',
      '#90EE90',
      '#98FB98',
      '#E0FFBF',
      '#000080',
      '#0000CD',
      '#1E90FF',
      '#4169E1',
      '#87CEEB',
      '#ADD8E6',
      '#B0E0E6',
      '#E6F2FF',
      '#4B0082',
      '#6A5ACD',
      '#7B68EE',
      '#8A2BE2',
      '#9370DB',
      '#BA55D3',
      '#D8BFD8',
      '#E6E6FA',
      '#800080',
      '#8B008B',
      '#9400D3',
      '#BA55D3',
      '#DA70D6',
      '#DDA0DD',
      '#EE82EE',
      '#F3E5F5',
      '#C71585',
      '#DB7093',
      '#FF1493',
      '#FF69B4',
      '#FFB6C1',
      '#FFC0CB',
      '#FFD1DC',
      '#FFF0F5',
      '#5D4037',
      '#6D4C41',
      '#795548',
      '#8D6E63',
      '#A1887F',
      '#BCAAA4',
      '#D7CCC8',
      '#EFEBE9',
      '#212121',
      '#424242',
      '#616161',
      '#757575',
      '#9E9E9E',
      '#BDBDBD',
      '#E0E0E0',
      '#F5F5F5',
      '#006064',
      '#00838F',
      '#0097A7',
      '#00ACC1',
      '#00BCD4',
      '#26C6DA',
      '#4DD0E1',
      '#B2EBF2',
      '#B44328',
      '#D35400',
      '#E67E22',
      '#FF7F50',
      '#FFA07A',
      '#FFB347',
      '#FFD700',
      '#FFDAB9',
      '#00504D',
      '#00695C',
      '#00796B',
      '#009688',
      '#4DB6AC',
      '#80CBC4',
      '#B2DFDB',
      '#E0F2F1',
      '#5D50A8',
      '#7E57C2',
      '#9C27B0',
      '#BA68C8',
      '#CE93D8',
      '#E1BEE7',
      '#F3E5F5',
      '#F8F4FF',
      '#5D1049',
      '#7D1B4E',
      '#9C2C5B',
      '#B93B5F',
      '#D44C73',
      '#E66B89',
      '#F18BA0',
      '#F8BBCF',
    ]);

    const state = reactive({
      colorSelectionOpen: false,
    });

    let selectedColor = ref('');

    const toggleDropdown = () => {
      state.colorSelectionOpen = !state.colorSelectionOpen;
    };

    const selectColor = (color) => {
      selectedColor.value = color;
      state.colorSelectionOpen = false;
      emit('optionSelected', color);
    };

    if (props.options.length > 0) {
      selectedColor.value = props.options;
    }

    return {
      state,
      toggleDropdown,
      selectedColor,
      selectColor,
      colorOptions,
    };
  },
};
</script>

<style scoped>
.dropdown-container {
  position: relative;
  display: inline-block;
}

.dark-theme .dropdown-button {
  color: white;
}

.dropdown-button:focus {
  outline: none;
}

.arrow {
  margin-left: 0.5em;
  transition: transform 0.2s;
}

.arrow.active {
  transform: rotate(-180deg);
}

.colors-container-wrapper {
  max-height: 30vh;
  overflow-y: scroll;
  list-style: none;
  margin: 0;
  border-radius: 5px;
  box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.2);
  z-index: 1;
  width: 100%;
}

.colors-grid-wrapper {
  display: grid;
  grid-template-columns: repeat(8, 1fr);
  gap: 0.5em;
  padding: 1em;
}

.light-theme .colors-container-wrapper {
  background: white;
}

.dark-theme .colors-container-wrapper {
  background: black;
}

.color-box {
  display: flex;
  justify-content: center;
  /* height: 40px;
  width: 40px; */
  border-radius: 50%;
  aspect-ratio: 1;
}

.selected-color-box {
  min-height: 15px;
  min-width: 15px;
  border-radius: 50%;
}
</style> -->
